import { useCallback, useEffect, useRef, useState } from 'react'

import { AuthoringUtils } from '@adobe/aem-spa-page-model-manager'

import StockService, { StockLevelStatus } from '../../../../services/stock'
import { getPreviewHeaders } from '../../../../utils/api-headers'
import type { ProductListConfig } from '../models'
import { STOCKS_CACHE_MAX_AGE, type Stocks } from '../utils'

interface UseStocksParams {
  config: ProductListConfig['config']
  gcodes: string[] | undefined
  ignoreStock: boolean | undefined
  setIsButtonDisabled: React.Dispatch<React.SetStateAction<boolean>>
}

export const useStocks = ({
  config,
  gcodes,
  ignoreStock,
  setIsButtonDisabled
}: UseStocksParams) => {
  const [stocks, setStocks] = useState<Stocks>({})
  const stocksTimestamp = useRef('')
  const firstRender = useRef(true)

  const setUpdatedStocks = useCallback(
    async (codes: string[]) => {
      if (
        !stocksTimestamp.current ||
        Math.abs(
          new Date().getTime() - new Date(stocksTimestamp.current).getTime()
        ) > STOCKS_CACHE_MAX_AGE
      ) {
        stocksTimestamp.current = new Date().toISOString()

        let stockData: Stocks = {}

        if (ignoreStock) {
          // When ignoreStock is true, set all products to in-stock with infinite stock level
          stockData = codes.reduce(
            (acc, code) => ({
              ...acc,
              [code]: {
                stockLevelStatus: StockLevelStatus.inStock,
                stockLevel: Number.MAX_SAFE_INTEGER // Infinite stock so minimumOrderQuantity never triggers OOS
              }
            }),
            {}
          )
        } else {
          const customHeaders = getPreviewHeaders()

          const products =
            (await StockService.getProductsStockData({
              ...config,
              articleList: codes,
              customHeaders
            })) || []

          stockData = products.reduce(
            (acc, { code, stock }) => ({
              ...acc,
              [code]: stock
            }),
            {}
          )
        }

        setStocks(stockData)
      }
    },
    [config, ignoreStock]
  )

  // Refresh stocks in AEM editor when ignoreStock config changes
  useEffect(() => {
    if (gcodes?.length && AuthoringUtils.isInEditor()) {
      stocksTimestamp.current = ''
      setUpdatedStocks(gcodes)
    }
  }, [ignoreStock]) // eslint-disable-line react-hooks/exhaustive-deps

  // Initial stock fetch + enable add-to-cart button
  useEffect(() => {
    setIsButtonDisabled(false)

    // for initial SSR render
    if (gcodes?.length && firstRender.current === true) {
      firstRender.current = false
      setUpdatedStocks(gcodes)
    }
  }, [gcodes, setUpdatedStocks]) // eslint-disable-line react-hooks/exhaustive-deps

  return { stocks, setUpdatedStocks }
}
